import {
  $applyNodeReplacement,
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { ReactNode } from 'react';

export type SerializedCommentImageNode = Spread<
  {
    type: 'comment-image';
    version: 1;
    attachmentId: string;
    src: string;
    altText: string;
  },
  SerializedLexicalNode
>;

export class CommentImageNode extends DecoratorNode<ReactNode> {
  __attachmentId: string;

  __src: string;

  __altText: string;

  static getType(): string {
    return 'comment-image';
  }

  static clone(node: CommentImageNode): CommentImageNode {
    return new CommentImageNode(
      node.__attachmentId,
      node.__src,
      node.__altText,
      node.__key
    );
  }

  constructor(
    attachmentId: string,
    src: string,
    altText: string,
    key?: NodeKey
  ) {
    super(key);
    this.__attachmentId = attachmentId;
    this.__src = src;
    this.__altText = altText;
  }

  static importJSON(serialized: SerializedCommentImageNode): CommentImageNode {
    return $createCommentImageNode({
      attachmentId: serialized.attachmentId ?? '',
      src: serialized.src ?? '',
      altText: serialized.altText ?? '',
    });
  }

  // Once `attachmentId` is set, the in-memory `__src` is a BE-injected signed
  // URL — never persist it. The server re-signs on every read. (If you ever
  // round-trip the OnChange output back into `initialState`, the editor will
  // briefly render the `(image)` placeholder until the next fetch.)
  exportJSON(): SerializedCommentImageNode {
    return {
      type: 'comment-image',
      version: 1,
      attachmentId: this.__attachmentId,
      src: this.__attachmentId ? '' : this.__src,
      altText: this.__altText,
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'block my-2';
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): ReactNode {
    if (!this.__src) {
      return (
        <span className="inline-block bg-muted text-muted-foreground text-xs px-2 py-1 rounded">
          (image)
        </span>
      );
    }
    return (
      <img
        src={this.__src}
        alt={this.__altText}
        className="max-w-full h-auto rounded-md my-2 inline-block"
        loading="lazy"
      />
    );
  }
}

export function $createCommentImageNode({
  attachmentId,
  src,
  altText = '',
}: {
  attachmentId: string;
  src: string;
  altText?: string;
}): CommentImageNode {
  return $applyNodeReplacement(
    new CommentImageNode(attachmentId, src, altText)
  );
}

export function $isCommentImageNode(
  node: LexicalNode | null | undefined
): node is CommentImageNode {
  return node instanceof CommentImageNode;
}
